/**
 * Mandar un SMS DESDE Deskte, no desde la app de mensajes del ordenador.
 *
 * ── EL FALLO QUE ARREGLA ────────────────────────────────────────────────────
 *
 * Lissette, 2026-09-01: *"cuando se trata de enviar por SMS firma o documentos
 * o cualquier otra, se abre un programa afuera como mensajería de la Mac o otra
 * cosa, no se envía directamente desde Deskte — inclusive que tiene Quo
 * integrado, ojo con eso"*.
 *
 * Cinco pantallas hacían esto:
 *
 *     window.open(`sms:${telefono}?body=...`)
 *
 * Un enlace `sms:` no manda nada: le pide al SISTEMA OPERATIVO que abra su app
 * de mensajes con el texto puesto. En un Mac eso abre Mensajes, y el SMS sale
 * —si sale— del teléfono personal de quien está sentado ahí. No queda en el
 * hilo del cliente, no respeta la lista de No-Textear, y el cliente ve un
 * número que no es el de la agencia.
 *
 * Y la tubería de verdad YA EXISTÍA: `POST /api/v1/phone/send-sms` manda por el
 * proveedor conectado del agente —Twilio, SignalWire y **Quo**, que es el que
 * ella tiene—, guarda el mensaje en el hilo y respeta la lista de No-Textear.
 * La usaban Tickets y las revisiones de renovación; estas cinco pantallas, no.
 *
 * ── EL ENLACE `sms:` NO DESAPARECE, PASA A SER EL PLAN B ────────────────────
 *
 * Si no hay proveedor conectado, o el cliente no está en el CRM todavía, o el
 * envío falla, se abre igual que antes — pero DICIENDO POR QUÉ. Quitarlo del
 * todo dejaría a la agente sin salida en mitad de un trámite; dejarlo como
 * primera opción es lo que estaba mal.
 *
 * OJO: `AgentCardPage.jsx` también usa `sms:` y ahí ES CORRECTO. Esa es la
 * tarjeta pública del agente: quien pulsa es el VISITANTE, abriendo su propio
 * teléfono para escribirle a la agencia. No se toca.
 */

import { sendClientSms } from './api.js';

const enApi = () => typeof window !== 'undefined' && !!window.__deskteAuth;

/** Abre la app de mensajes del sistema. El plan B, nunca el plan A. */
export function abrirEnlaceSms(telefono, mensaje) {
  const limpio = String(telefono || '').replace(/[^0-9+]/g, '');
  if (!limpio || typeof window === 'undefined') return false;
  window.open(`sms:${limpio}?body=${encodeURIComponent(mensaje || '')}`, '_blank', 'noopener,noreferrer');
  return true;
}

/**
 * Por qué no salió por Deskte, dicho con la verdad y con el siguiente paso.
 *
 * Un solo sitio: lo comparten las cinco pantallas, y dos copias acabarían
 * diciendo cosas distintas del mismo fallo (E-138).
 */
export function motivoDeSmsFallido(motivo, lang) {
  const es = lang === 'es';
  switch (String(motivo || '')) {
    case 'no-integration':
      return es
        ? 'No hay proveedor de SMS conectado, así que se abrió tu app de mensajes. Conecta Quo o Twilio en Ajustes → SMS y Llamadas para mandarlos desde Deskte.'
        : 'No SMS provider is connected, so your messaging app opened instead. Connect Quo or Twilio under Settings → SMS & Calls to send from Deskte.';
    case 'suppressed':
      // NO se abre el enlace en este caso — ver `enviarSmsAlCliente`.
      return es
        ? 'Este número está en tu lista de No-Textear y no se le escribió. Quítalo de esa lista si quieres volver a mandarle mensajes.'
        : "This number is on your Do-Not-Text list and wasn't messaged. Remove it from that list to text them again.";
    case 'no-phone':
      return es
        ? 'Este cliente no tiene teléfono en la ficha.'
        : 'This client has no phone number on file.';
    default:
      return es
        ? 'No se pudo mandar el SMS desde Deskte, así que se abrió tu app de mensajes.'
        : "The SMS couldn't be sent from Deskte, so your messaging app opened instead.";
  }
}

/**
 * Manda el SMS por el proveedor del agente. Si no se puede, abre la app de
 * mensajes y explica por qué.
 *
 * @returns {Promise<{enviado: boolean, texto?: string, seAbrio?: boolean}>}
 *   `enviado`  salió por Deskte, quedó en el hilo del cliente.
 *   `texto`    qué decirle a la agente cuando NO salió por Deskte.
 *   `seAbrio`  se abrió la app de mensajes como plan B.
 */
export async function enviarSmsAlCliente({ clientId, telefono, mensaje, lang }) {
  // Sin cliente en el CRM (o sin sesión) no hay a quién mandárselo por la vía
  // buena: la ruta del servidor trabaja sobre una ficha. Plan B directo, y sin
  // asustar con un error — no ha fallado nada.
  if (!clientId || !enApi()) {
    const seAbrio = abrirEnlaceSms(telefono, mensaje);
    return { enviado: false, seAbrio, texto: '' };
  }
  try {
    await sendClientSms(clientId, { message: mensaje });
    return { enviado: true };
  } catch (err) {
    const motivo = String(err?.message || '');
    // En la lista de No-Textear NO se abre el enlace: sería saltarse por la
    // puerta de atrás lo que el propio CRM acaba de impedir, y esa lista
    // existe porque el cliente pidió que no le escribieran (STOP/BAJA).
    if (motivo.includes('suppressed')) {
      return { enviado: false, seAbrio: false, texto: motivoDeSmsFallido('suppressed', lang) };
    }
    const seAbrio = abrirEnlaceSms(telefono, mensaje);
    return { enviado: false, seAbrio, texto: motivoDeSmsFallido(motivo, lang) };
  }
}
